import { toast } from "sonner";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const KEY = "rpopro_ats_scores";
const EVT = "rpopro:ats";
const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; } };
const write = (all) => { localStorage.setItem(KEY, JSON.stringify(all)); window.dispatchEvent(new CustomEvent(EVT)); };

export const getHistory = (id) => (id && read()[id]) || [];

let currentResume = null;
export const setCurrentResume = (name) => { currentResume = name || null; };
export const getCurrentResume = () => currentResume;

export const recordScore = (id, score, toolId) => {
  if (!id || score == null) return;
  const entry = { score: Math.round(score), at: new Date().toISOString(), ...(currentResume && { resume: currentResume }) };
  const all = read();
  const prev = all[id]?.at(-1)?.score;
  all[id] = [...(all[id] || []), entry].slice(-20);
  write(all);
  const min = getPassMin();
  if (prev != null && prev < min && entry.score >= min) {
    setTimeout(() => window.dispatchEvent(new CustomEvent("rpopro:ats-cross", { detail: { jobId: id, score: entry.score, prev } })), 120);
    toast.success(`You're ready to apply — ${prev}% → ${entry.score}%`, { description: `This job just passed your ${min}% pass score.`, id: `cross-${id}` });
  }
  api.post("/ats-scores", { job_id: id, score: entry.score, tool_id: toolId || null, resume_name: currentResume }).catch(() => {});
};

export const bestResume = (history) => {
  const by = {};
  for (const r of history) if (r.resume && (!by[r.resume] || r.at > by[r.resume].at)) by[r.resume] = r;
  const list = Object.entries(by).map(([resume, r]) => ({ resume, score: r.score }));
  if (list.length < 2) return null;
  return list.sort((a, b) => b.score - a.score)[0];
};

let synced = false;
export const syncScores = async () => {
  if (synced) return;
  synced = true;
  try {
    const { data } = await api.get("/ats-scores");
    const remote = data?.scores || {};
    const all = read();
    for (const [id, runs] of Object.entries(remote)) {
      if (!Array.isArray(runs)) continue;
      const valid = runs.filter((r) => r && typeof r.at === "string" && Number.isFinite(Number(r.score)));
      const seen = new Set((all[id] || []).map((r) => r.at));
      all[id] = [...(all[id] || []), ...valid.filter((r) => !seen.has(r.at)).map((r) => ({ score: Math.round(Number(r.score)), at: r.at, ...(r.resume_name && { resume: r.resume_name }) }))].sort((a, b) => a.at.localeCompare(b.at)).slice(-20);
    }
    write(all);
  } catch (e) { if (!e.response) synced = false; }
};

export const useAtsHistory = (id) => {
  const [h, setH] = useState(() => getHistory(id));
  useEffect(() => {
    const f = () => setH(getHistory(id));
    f();
    syncScores();
    window.addEventListener(EVT, f);
    window.addEventListener("storage", f);
    return () => { window.removeEventListener(EVT, f); window.removeEventListener("storage", f); };
  }, [id]);
  return h;
};

const PASS_KEY = "rpopro_pass_min";
export const getPassMin = () => { const n = Number(localStorage.getItem(PASS_KEY)); return n >= 50 && n <= 95 ? n : 70; };
export const setPassMin = (n) => { localStorage.setItem(PASS_KEY, String(n)); window.dispatchEvent(new CustomEvent(EVT)); };
export const usePassMin = () => {
  const [v, setV] = useState(getPassMin);
  useEffect(() => {
    const f = () => setV(getPassMin());
    window.addEventListener(EVT, f);
    window.addEventListener("storage", f);
    return () => { window.removeEventListener(EVT, f); window.removeEventListener("storage", f); };
  }, []);
  return v;
};

export const isReady = (jobId, min = getPassMin()) => { const s = getHistory(jobId).at(-1)?.score; return s != null && s >= min; };
export const useAtsTick = () => {
  const [, setT] = useState(0);
  useEffect(() => {
    const f = () => setT((t) => t + 1);
    window.addEventListener(EVT, f);
    window.addEventListener("storage", f);
    syncScores();
    return () => { window.removeEventListener(EVT, f); window.removeEventListener("storage", f); };
  }, []);
  return getPassMin();
};
export const allHistory = () => read();
